import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Filter, X } from "lucide-react";
import { format, startOfDay, subDays, startOfWeek, startOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

const datePresets = [
  { value: "all", label: "Todas as datas" },
  { value: "today", label: "Hoje" },
  { value: "week", label: "Ultima semana" },
  { value: "month", label: "Ultimo mes" },
  { value: "custom", label: "Personalizado" },
];

const priorityOptions = [
  { value: "all", label: "Todas" },
  { value: "urgente", label: "Urgente" },
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baixa", label: "Baixa" },
];

export function useFilters() {
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [clientId, setClientId] = useState("all");
  const [priority, setPriority] = useState("all");

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case "today":
        return { from: startOfDay(now), to: now };
      case "week":
        return { from: subDays(startOfDay(now), 7), to: now };
      case "month":
        return { from: subDays(startOfDay(now), 30), to: now };
      case "custom":
        return { from: customFrom, to: customTo };
      default:
        return { from: null, to: null };
    }
  }, [datePreset, customFrom, customTo]);

  const hasActiveFilters = datePreset !== "all" || clientId !== "all" || priority !== "all";

  const clearFilters = () => {
    setDatePreset("all");
    setCustomFrom(null);
    setCustomTo(null);
    setClientId("all");
    setPriority("all");
  };

  const filterOrders = (orders) => {
    return orders.filter((order) => {
      if (dateRange.from) {
        const orderDate = new Date(order.created_at);
        if (orderDate < dateRange.from) return false;
        if (dateRange.to && orderDate > dateRange.to) return false;
      }
      if (clientId !== "all" && order.client_id !== clientId) return false;
      if (priority !== "all" && order.priority !== priority) return false;
      return true;
    });
  };

  return {
    datePreset, setDatePreset,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    clientId, setClientId,
    priority, setPriority,
    dateRange, hasActiveFilters, clearFilters, filterOrders,
  };
}

export default function FilterBar({ filters, clients = [], showClientFilter = true, showPriorityFilter = true, className = "" }) {
  const {
    datePreset, setDatePreset,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    clientId, setClientId,
    priority, setPriority,
    hasActiveFilters, clearFilters,
  } = filters;

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="filter-bar">
      <Filter className="h-4 w-4 text-muted-foreground shrink-0" />

      {/* Date Filter */}
      <Select value={datePreset} onValueChange={setDatePreset}>
        <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="date-filter">
          <SelectValue placeholder="Periodo" />
        </SelectTrigger>
        <SelectContent>
          {datePresets.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom date pickers */}
      {datePreset === "custom" && (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="date-from-btn">
                <CalendarIcon className="h-3 w-3" />
                {customFrom ? format(customFrom, "dd/MM/yy") : "De"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} locale={ptBR} />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="date-to-btn">
                <CalendarIcon className="h-3 w-3" />
                {customTo ? format(customTo, "dd/MM/yy") : "Ate"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={customTo} onSelect={setCustomTo} locale={ptBR} />
            </PopoverContent>
          </Popover>
        </>
      )}

      {/* Client Filter */}
      {showClientFilter && (
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="client-filter">
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os clientes</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Priority Filter */}
      {showPriorityFilter && (
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="priority-filter">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            {priorityOptions.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Clear */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground" onClick={clearFilters} data-testid="clear-filters-btn">
          <X className="h-3 w-3" /> Limpar
        </Button>
      )}
    </div>
  );
}
